/**
 * The time messages are stamped with, and the time they are shown in.
 *
 * A message carries the moment it was written, and that moment comes from
 * the device that wrote it. A phone set to the wrong time zone with its clock
 * wound by hand to look right — common wherever automatic time is switched
 * off — is hours away from everybody else in real terms, and every message it
 * sent showed hours off on the other end. Nothing in the chat itself can tell
 * which of two devices is right, so the server is asked: /api/time answers
 * with its own clock, and the difference is kept here.
 *
 * Two uses, two directions:
 *
 *  - now() is the server's time, for stamping. Every device stamps in the
 *    same frame, whatever its own clock says.
 *  - toDevice(ts) turns such a stamp back into this device's clock, for
 *    showing. A phone whose clock is wrong reads its time off the status bar
 *    all day; a message written "now" should show the time the status bar
 *    shows, not the real one.
 *
 * Until the server has answered, or when it never does, the offset is 0 and
 * both are the device's own clock — what every build before this one did.
 */

// Under this the two clocks are taken to agree: the measurement itself is
// only as good as half a round trip, and a few seconds either way do not
// move a message.
const AGREE = 2000
// A round trip slower than this says too little about either clock.
const SLOW = 8000

let offset = 0 // the server's clock minus this device's, ms

/** The server's time, as best known. */
export function now () {
  return Date.now() + offset
}

/** How far this device's clock is behind the server's (negative: ahead). */
export function clockOffset () {
  return offset
}

/** A stamp in the server's time, as this device's clock would read it. */
export function toDevice (ts) {
  return ts ? ts - offset : ts
}

/** A moment read off this device's clock, in the server's time. */
export function fromDevice (ts) {
  return ts ? ts + offset : ts
}

/** For tests: pretend the server said this. */
export function setClockOffset (value) {
  offset = Math.abs(value) < AGREE ? 0 : Math.round(value)
}

/**
 * Ask the server what time it is. Resolves to the offset now in use, or
 * null when the server could not be asked (the previous offset stays).
 */
export async function syncClock (url, { fetchImpl = globalThis.fetch, timeout = SLOW } = {}) {
  const base = String(url || '').replace(/\/+$/, '')
  if (!base || typeof fetchImpl !== 'function') return null
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), timeout) : null
  const sent = Date.now()
  let body
  try {
    const response = await fetchImpl(base + '/api/time', {
      cache: 'no-store',
      signal: controller ? controller.signal : undefined
    })
    if (!response.ok) return null
    body = await response.json()
  } catch {
    return null
  } finally {
    if (timer) clearTimeout(timer)
  }
  const received = Date.now()
  const server = Number(body && body.now)
  if (!Number.isFinite(server) || server <= 0) return null
  if (received - sent > SLOW) return null
  // The server read its clock somewhere in the middle of the round trip.
  setClockOffset(server + (received - sent) / 2 - received)
  return offset
}
